import { useCallback, useState, useEffect } from 'preact/hooks'
import { CarouselOptions, SlideDimensions } from './types'
import { computeCarouselDimensions } from './carousel-helpers'
import { resizeManager } from './resize-manager'

/**
 * Custom hook for managing carousel slide dimensions
 * Handles calculating dimensions based on carousel options and DOM elements
 *
 * @param carouselOptions - The carousel configuration options
 * @param carouselSelector - Unique selector for the carousel element
 * @param outerCarouselWrapperSelector - Unique selector for the outer wrapper element
 * @param initialDimensions - Initial slide dimensions to use as fallback
 * @returns An object containing slideDimensions and calculateDimensions function
 */
export function useCarouselDimensions(
  carouselOptions: CarouselOptions,
  carouselSelector: string,
  outerCarouselWrapperSelector: string,
  initialDimensions: SlideDimensions = { width: '250px', marginRight: '16px' }
) {
  const [slideDimensions, setSlideDimensions] = useState<SlideDimensions>(initialDimensions)

  const calculateDimensions = useCallback(() => {
    const carouselSlideDimensions = computeCarouselDimensions({
      options: carouselOptions,
      carouselSelector,
      outerCarouselWrapperSelector
    })

    if (!carouselSlideDimensions) {
      // DOM not ready yet; skip for now
      return
    }

    setSlideDimensions(carouselSlideDimensions)
  }, [carouselOptions, carouselSelector, outerCarouselWrapperSelector])

  // Initial calculation and resize listener
  useEffect(() => {
    const timeoutId = setTimeout(calculateDimensions, 100)

    resizeManager.register(calculateDimensions)

    return () => {
      clearTimeout(timeoutId)
      resizeManager.unregister(calculateDimensions)
    }
  }, [calculateDimensions])

  return { slideDimensions, calculateDimensions }
}
